import { convexQuery } from "@convex-dev/react-query";
import type { Id } from "@convex/_generated/dataModel";
import { useMutation, useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Fragment, useState } from "react";
import { toast } from "sonner";

import { itemsGetOne, itemsRemove, itemsRemoveFromCategory } from "~/api-refs";
import { ModalLoadingDialog } from "~/components/shared/modal-loading-dialog";
import { Button } from "~/components/ui/button";
import { Checkbox } from "~/components/ui/checkbox";
import { Dialog, DialogContent, DialogTitle } from "~/components/ui/dialog";
import { convexClient } from "~/integrations/convex/provider";
import { toConvexTableId } from "~/lib/convex-table-id";
import { logger } from "~/lib/logger";
import { ProductEvents } from "~/lib/posthog";

export const Route = createFileRoute(
  "/_authenticated/menus/$menuId/items/$itemId/delete"
)({
  component: DeleteItemModal,
});

function DeleteItemModal() {
  const { menuId, itemId } = Route.useParams();
  const navigate = useNavigate();
  const [categoriesSelected, setCategoriesSelected] = useState<string[]>([]);

  // Fetch item data
  const { data: item, isPending } = useQuery(
    convexQuery(itemsGetOne, { itemId: toConvexTableId("items", itemId) })
  );

  const removeFromCategoryMutation = useMutation({
    mutationFn: ({
      itemId: targetItemId,
      categoryId,
    }: {
      itemId: Id<"items">;
      categoryId: Id<"categories">;
    }) => {
      return convexClient.mutation(itemsRemoveFromCategory, {
        itemId: targetItemId,
        categoryId,
      });
    },
  });

  const removeItemMutation = useMutation({
    mutationFn: ({ itemId: targetItemId }: { itemId: Id<"items"> }) => {
      return convexClient.mutation(itemsRemove, { itemId: targetItemId });
    },
  });

  const handleClose = () => {
    void navigate({ to: `/menus/${menuId}/categories` });
  };

  const isDeleting =
    removeFromCategoryMutation.isPending || removeItemMutation.isPending;

  const onDelete = async () => {
    if (isDeleting || !item) {
      return;
    }

    const isInMultipleCategories = item.categoryRelations.length > 1;

    try {
      if (isInMultipleCategories) {
        for (const categoryId of categoriesSelected) {
          await removeFromCategoryMutation.mutateAsync({
            itemId: toConvexTableId("items", item.id),
            categoryId: toConvexTableId("categories", categoryId),
          });
        }
        const categoriesText =
          categoriesSelected.length === 1 ? "category" : "categories";
        toast.success(
          `Item "${item.name}" has been removed from ${categoriesSelected.length} ${categoriesText}.`
        );
      } else {
        await removeItemMutation.mutateAsync({
          itemId: toConvexTableId("items", item.id),
        });
        // Track menu item deletion in PostHog
        ProductEvents.menuItemDeleted(item.id);
        toast.success(`Item "${item.name}" has been successfully deleted.`);
      }

      // Navigate immediately after deletion to prevent 404
      await navigate({ to: `/menus/${menuId}/categories`, replace: true });
    } catch (error) {
      logger.exception("item_deletion_failed", error, {
        itemId: item?.id,
        itemName: item?.name,
      });
      toast.error(`Failed to delete item "${item?.name}". Please try again.`);
    }
  };

  const updateItemSelection = (categoryId: string) => {
    if (categoriesSelected.includes(categoryId)) {
      const updatedSelectedCategories = categoriesSelected.filter(
        (category: string) => category !== categoryId
      );
      setCategoriesSelected(updatedSelectedCategories);
    } else {
      setCategoriesSelected([...categoriesSelected, categoryId]);
    }
  };

  if (isPending || !item) {
    return (
      <ModalLoadingDialog
        message="Loading item details..."
        onOpenChange={handleClose}
      />
    );
  }

  const isInMultipleCategories = item.categoryRelations.length > 1;

  // Calculate total affected menus across all categories
  const affectedMenus = new Set(
    item.categoryRelations.flatMap(
      (rel: (typeof item.categoryRelations)[number]) =>
        rel.category.menuRelations.map(
          (menu: (typeof rel.category.menuRelations)[number]) => menu.menu.name
        )
    )
  );
  const affectedMenuCount = affectedMenus.size;

  return (
    <Dialog onOpenChange={handleClose} open>
      <DialogContent className="max-w-lg">
        <div className="flex w-full">
          <div className="flex w-full flex-col items-center">
            <DialogTitle className="h2 text-foreground w-fit leading-9">
              {isInMultipleCategories ? "Delete shared item" : "Delete item"}
            </DialogTitle>
            <h3 className="body text-muted-foreground mt-1 text-center text-balance">
              {isInMultipleCategories
                ? "This item is shared with multiple categories. Select all or specific categories to remove this item from."
                : `Are you sure you want to delete ${item?.name}? This action cannot be undone.`}
            </h3>
            {affectedMenuCount > 1 && (
              <div className="border-warning/20 bg-warning/10 mt-3 rounded-lg border p-3">
                <p className="label text-warning">
                  This item appears in {affectedMenuCount} menus
                </p>
                <p className="caption text-warning mt-1">
                  {isInMultipleCategories
                    ? "Removing from selected categories will affect all menus sharing those categories."
                    : "Deleting this item will remove it from all menus where it appears."}
                </p>
              </div>
            )}
            {isInMultipleCategories && (
              <div className="mt-5 flex w-full flex-col items-start gap-3">
                {item.categoryRelations.map(
                  (category: (typeof item.categoryRelations)[number]) => {
                    return (
                      <Fragment key={category.category._id}>
                        <div className="border-border flex w-full gap-2 rounded-xl border p-3">
                          <Checkbox
                            checked={categoriesSelected.includes(
                              category.category._id
                            )}
                            className="mt-1"
                            onCheckedChange={() =>
                              updateItemSelection(category.category._id)
                            }
                          />
                          <div>
                            <div className="flex w-fit space-x-2.5">
                              <div className="flex gap-2">
                                {category.category.name}
                                {category.category.menuRelations.length > 1 && (
                                  <div className="caption bg-info/10 text-info rounded-full px-2 py-1">
                                    Shared
                                  </div>
                                )}
                              </div>
                            </div>
                            <div className="flex gap-1 pt-2">
                              {category.category.menuRelations.map(
                                (
                                  menu: (typeof category.category.menuRelations)[number]
                                ) => (
                                  <div
                                    className="caption bg-menu-bg text-menu rounded-full px-2 py-1"
                                    key={menu.menu._id}
                                  >
                                    <span>{menu.menu.name}</span>
                                  </div>
                                )
                              )}
                            </div>
                          </div>
                        </div>
                      </Fragment>
                    );
                  }
                )}
              </div>
            )}

            <div className="mt-5 flex w-full gap-3">
              <Button
                className="w-full"
                onClick={handleClose}
                type="button"
                variant="outline"
              >
                Cancel
              </Button>
              <Button
                className="w-full"
                disabled={
                  isDeleting ||
                  (isInMultipleCategories && categoriesSelected.length === 0)
                }
                onClick={onDelete}
                type="button"
                variant="destructive"
              >
                Delete item
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
